import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';

import './mobile-search-result-style.scss';

import { SearchQueryContext, searchQueryType } from '@src/providers/SearchQueryProvider';

import TopButton from '@src/componentsNew/Buttons/TopButton/TopButton';
import SortListIcon from '@src/componentsNew/Icons/Icons/SortListIcon';
import FilterListIcon from '@src/componentsNew/Icons/Icons/FilterListIcon';
import SearchIcon from '@src/componentsNew/Icons/Icons/SearchIcon';
import WishlistIcon from '@src/componentsNew/Icons/Icons/WishlistIcon';
import CartQuantityIcon from '@src/componentsNew/Icons/CartQuantityIcon/CartQuantityIcon';
import ArrowBackIcon from '@src/componentsNew/Icons/Icons/ArrowBackIcon';

import Chip from '@src/componentsNew/Chip';
import Typography from '@src/componentsNew/Typography/Typography';
import MobileCustomHeader from '@src/componentsNew/Header/CustomHeader/MobileCustomHeader';
import BrandItem from '@src/componentsNew/ListItem/BrandItem/BrandItem';
import SearchResultSkeleton from '@src/componentsNew/Skeleton/SearchResultSkeleton';
import MobileProductListSortingFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListSortingFilter/MobileProductListSortingFilter';
import MobileProductListFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListFilter/MobileProductListFilter';
import MobileBasicProductList from '@src/componentsNew/ProductList/Basic/MobileBasicProductList';
import MobileProductDetailRecommendList from '@src/componentsNew/ProductList/ProductDetailRecommend/MobileProductDetailRecommendList';

import useIsComDomain from '@src/hooks/useIsComDomain';
import useScroll from '@src/hooks/useScroll';

import { RecommendType } from '@components/ProductPage/ProductDetailPage/queries';
import { UI_COMPONENT } from '@queries';
import { uiComponent } from '@src/queries/__generated__/uiComponent';

import { productsVariables } from '@components/ProductPage/ProductList/__generated__/products';
import { StockAvailability, ProductOrderField, OrderDirection } from '__generated__/globalTypes';
import { pixelSearch } from '@src/utils/pixel';
import { goToUrl } from '@src/utils/urlCheck';
import { MOBILE_PRODUCT_LIST_REQUEST_COUNT } from '@src/utils/constants';
import { uiComponentVariables } from '@src/core/config';
import { getDBIdFromGraphqlId, getSlugFromText } from '@utils';
import { elasticSearchBrandResult, elasticSearchTagResult } from '@src/utils/elasticSearch';
import { wishListUrl, searchUrl, cartUrl, productsUrl, mobileShopTabUrl } from '@src/app/routes';

interface ILocationState {
  prevSearchQuery: string;
}

const MobileSearchResultPage = () => {
  const history = useHistory();
  const isComDomain = useIsComDomain();
  const { isScrolled, scrollTop } = useScroll();
  const { searchQuery } = useContext<searchQueryType>(SearchQueryContext);

  const initialVariables = {
    stockAvailability: StockAvailability.IN_STOCK,
    sortBy: {
      field: ProductOrderField.POPULARITY,
      direction: OrderDirection.DESC,
    },
    query: searchQuery,
    first: MOBILE_PRODUCT_LIST_REQUEST_COUNT,
    ...(isComDomain && { isMgs: true }),
  };

  const [variables, setVariables] = useState<productsVariables>(initialVariables);
  const [numOfProducts, setNumOfProducts] = useState<number>(0);
  const [isSortingFilterModalOpen, setIsSortingFilterModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // elastic search 관련
  const category = JSON.parse(localStorage.getItem('category') || '[]');
  const categoryFilter = (el) => el.node.name.toLowerCase().includes(searchQuery?.toLowerCase());

  const [firstProduct, setFirstProduct] = useState(null); // 첫 번째 상품 정보
  const [isAllProductsLoaded, setIsAllProductsLoaded] = useState(false); // 모든 상품 로드 여부

  const handleFirstProductLoaded = useCallback((product) => {
    setFirstProduct(product);
  }, []);

  const handleAllProductsLoaded = useCallback(() => {
    if (!isAllProductsLoaded) {
      setIsAllProductsLoaded(true);
    }
  }, [isAllProductsLoaded]);

  // 브랜드 관련 상태
  const [isBrandLoading, setIsBrandLoading] = useState(false);
  const [searchedBrands, setSearchedBrands] = useState([]);

  // 태그 관련 상태
  const [isTagLoading, setIsTagLoading] = useState(false);
  const [searchedTags, setSearchedTags] = useState([]);

  // 브랜드 정보만 조회하는 함수
  const fetchBrands = useCallback(async () => {
    if (!searchQuery || searchQuery.trim().length < 2) return;

    setIsBrandLoading(true);
    try {
      const brands = await elasticSearchBrandResult(searchQuery);

      setSearchedBrands(brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setIsBrandLoading(false);
    }
  }, [searchQuery]);

  // 태그 정보만 조회하는 함수
  const fetchTags = useCallback(async () => {
    if (!searchQuery || searchQuery.trim().length < 2) return;

    setIsTagLoading(true);
    try {
      const tags = await elasticSearchTagResult(searchQuery);

      setSearchedTags(tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setIsTagLoading(false);
    }
  }, [searchQuery]);

  const [
    getHomeTabRankCategory,
    { data: homeTabRankCategory, loading: homeTabRankCategoryLoading },
  ] = useLazyQuery<uiComponent>(UI_COMPONENT, {
    fetchPolicy: 'no-cache',
  });

  // 초기 로드 시 데이터 가져오기
  useEffect(() => {
    pixelSearch(searchQuery);
    fetchBrands();

    // 검색어가 변경되면 태그 관련 상태 초기화
    setSearchedTags([]);
    setFirstProduct(null);
    setIsAllProductsLoaded(false);
  }, [searchQuery, fetchBrands]);

  // 모든 상품이 로드되었을 때 태그 정보 가져오기
  useEffect(() => {
    if (isAllProductsLoaded) {
      fetchTags();
      getHomeTabRankCategory({ variables: uiComponentVariables.homeTabRankCategory });
    }
  }, [isAllProductsLoaded, fetchTags]);

  const handleClickSearchQueryText = () => {
    history.push({
      pathname: searchUrl,
      state: {
        prevSearchQuery: searchQuery,
      } as ILocationState,
    });
  };

  const handleClickCategoryListItem = (category) => {
    history.push(
      `${productsUrl}category/${getSlugFromText(category.name)}-${getDBIdFromGraphqlId(category.id, 'Category')}`,
    );
  };

  const handleClickTagListItem = (tagItem) => {
    const tag = tagItem['first_tag']['hits']['hits'][0]['_source']['tag'];

    history.push(
      `${productsUrl}tag/${tag.toLowerCase().replace(/[\/ ]/g, '-')}-${getDBIdFromGraphqlId(
        tagItem['key'],
      ).toString()}?name=${tag}`,
    );
  };

  const handleClickUIComponentItem = (item) => {
    goToUrl(history, item.link);
  };

  const handleSortingFilterModalOpen = () => {
    setIsSortingFilterModalOpen(true);
  };

  const handleSortingFilterModalClose = () => {
    setIsSortingFilterModalOpen(false);
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  return (
    <div id="mobile-search-result-wrapper">
      <div className="fixed-area">
        <MobileCustomHeader
          type="left"
          isShowBackBtn={true}
          backBtnColor="#21242A7A"
          pageTitle={searchQuery}
          pageTitleColor="#212121"
          pageTitleVariant="h8"
          pageTitleComponent="h3"
          onPageTitleClick={handleClickSearchQueryText}
          rightChild={
            <>
              <SearchIcon onClick={() => history.push(searchUrl)} />
              <WishlistIcon onClick={() => history.push(wishListUrl)} />
              <CartQuantityIcon isMobile={true} onClick={() => history.push(cartUrl)} />
            </>
          }
        />
      </div>

      <div className="main-area">
        {/* Category */}
        {category.filter(categoryFilter).length ? (
          <div className="additional-search-result-wrapper category">
            {category
              .filter(categoryFilter)
              .slice(0, 3)
              .map((category, idx) => (
                <Chip
                  key={idx}
                  className="category-item"
                  name={category['node'].name}
                  onClick={() => handleClickCategoryListItem(category.node)}
                />
              ))}
          </div>
        ) : null}

        {/* Brand */}
        {isBrandLoading ? (
          <SearchResultSkeleton isMobile={true} />
        ) : searchedBrands?.length > 0 ? (
          <div className="additional-search-result-wrapper brand">
            <Typography variant="subtitle2" component="h3" style={{ color: '#11141D99' }}>
              Brand
            </Typography>

            <BrandItem isMobile={true} brand={searchedBrands[0]} />
          </div>
        ) : null}

        <div className="product-list-container">
          <div className="sorting-filter-area">
            <div className="total-count-area">
              <Typography
                variant="subtitle2"
                component="span"
                style={{ color: '#11141D99' }}
              >{`${numOfProducts}`}</Typography>
              <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
                Products
              </Typography>
            </div>

            <div className="sorting-icon-wrapper">
              <SortListIcon onClick={handleSortingFilterModalOpen} />
              <FilterListIcon onClick={handleFilterModalOpen} />
            </div>
          </div>

          <MobileBasicProductList
            initialVariables={initialVariables}
            variables={variables}
            setNumOfProducts={setNumOfProducts}
            isSearchResult={true}
            onFirstProductLoaded={handleFirstProductLoaded}
            onAllProductsLoaded={handleAllProductsLoaded}
          />
        </div>

        {isAllProductsLoaded && (
          <>
            {/* Tag */}
            {isTagLoading ? (
              <SearchResultSkeleton isMobile={true} />
            ) : searchedTags?.length > 0 ? (
              <div className="additional-search-result-wrapper tag">
                <Typography
                  cName="additional-search-result-title"
                  variant="subtitle2"
                  component="h3"
                  style={{ color: '#11141D99' }}
                >
                  Tags
                </Typography>

                <div className="additional-search-result-tag-wrapper">
                  <div className="additional-search-result-row-tag-wrapper">
                    {searchedTags.slice(0, 5).map((evenTag, i) => (
                      <Chip
                        key={i}
                        className="tag-item"
                        name={evenTag?.first_tag?.hits?.hits[0]['_source'].tag}
                        onClick={() => handleClickTagListItem(evenTag)}
                      />
                    ))}
                  </div>

                  {searchedTags.length > 5 && (
                    <div className="additional-search-result-row-tag-wrapper">
                      {searchedTags.slice(5, 10).map((tag, i) => (
                        <Chip
                          key={i}
                          className="tag-item"
                          name={tag?.first_tag?.hits?.hits[0]['_source'].tag}
                          onClick={() => handleClickTagListItem(tag)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* 첫 상품 기반 추천 상품 */}
            {firstProduct && (
              <MobileProductDetailRecommendList
                title="Others Frequently Bought"
                productId={firstProduct.id}
                recommendType={RecommendType.User}
                titleVariant="subtitle1"
              />
            )}

            {/* home tab rank category */}
            {homeTabRankCategoryLoading ? (
              <SearchResultSkeleton isMobile={true} type="ui-component" />
            ) : homeTabRankCategory?.uicomponent?.items?.filter((item) => item?.isActive)?.length > 0 ? (
              <div className="additional-search-result-wrapper ui-component">
                <Typography
                  cName="additional-search-result-title"
                  variant="subtitle2"
                  component="h3"
                  style={{ color: '#11141D99' }}
                >
                  What are you looking for?
                </Typography>

                <div className="additional-search-result-ui-component-wrapper">
                  {homeTabRankCategory?.uicomponent?.items
                    ?.filter((item) => item?.isActive)
                    ?.map((item, i) => (
                      <div key={item.id} className="ui-component-item" onClick={() => handleClickUIComponentItem(item)}>
                        <div className="ui-component-item-image">
                          <img src={item.imageMobileUrl || item.imageWebUrl} alt={item.name} />
                        </div>

                        <Typography
                          cName="ui-component-item-name"
                          variant="body2"
                          component="span"
                          style={{ color: '#090A0FDE' }}
                        >
                          {item.name}
                        </Typography>
                      </div>
                    ))}

                  <div className="ui-component-item" onClick={() => history.push(mobileShopTabUrl)}>
                    <div className="ui-component-item-image">
                      <ArrowBackIcon />
                    </div>

                    <Typography
                      cName="ui-component-item-name"
                      variant="body2"
                      component="span"
                      style={{ color: '#090A0FDE' }}
                    >
                      All Categories
                    </Typography>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      {isSortingFilterModalOpen && (
        <MobileProductListSortingFilter
          open={isSortingFilterModalOpen}
          onClose={handleSortingFilterModalClose}
          variables={variables}
          setVariables={setVariables}
        />
      )}

      {isFilterModalOpen && (
        <MobileProductListFilter
          open={isFilterModalOpen}
          onClose={handleFilterModalClose}
          variables={variables}
          setVariables={setVariables}
        />
      )}

      {isScrolled && <TopButton isMobile={true} onClick={scrollTop} />}
    </div>
  );
};

export default MobileSearchResultPage;
